import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  Flex,
  Header,
  SearchableMultiSelectField,
  Text,
  TextInputField,
} from '@electricjs/arc';
import { EmployeeStatusEnum } from '@electricjs/core_entity-client';

import { useFlags } from 'launchdarkly-react-client-sdk';

import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useOrganizationEmployees } from '@/hooks/useOrganizationEmployees';
import { AddEmployeeParams } from '@/pages/Onboardings/NewOnboarding';
import { useLazyGetEmployeeByEmailQuery } from '@/redux/slices/employeeApiSlice';
import EmployeeManagerSelector from '@/components/People/EmployeeProfile/EmployeeManagerSelectorField';
import { isFetchBaseQueryError } from '@/redux/slices/helpers/errorHandlersHelpers';
import { getFullName } from 'common/utils/getFullName';
import { FormActionsContainer } from './common';
import { OnboardingFormData, OnboardingStep } from './types';
import { transformEmployeesToOptions } from '@/components/People/transformOptions';
import { useIsAdpWfnCanadaConnectedOrg } from '@/hooks/useIsAdpWfnCanadaConnectedOrg';
import { EmployeeSelectOption } from '@/components/EmployeeSelectOption';
import { DateField } from 'common/DateField';

type EmployeeInformationProps = {
  addEmployee: (params: AddEmployeeParams) => void;
  isNewHire: boolean;
  canOnboard: boolean;
  isMultiLicense: boolean;
  hasAvailableLicenses: boolean;
  handleSetInactive: () => void;
};

const EmployeeInformation = ({
  addEmployee,
  isNewHire,
  canOnboard,
  isMultiLicense,
  hasAvailableLicenses,
  handleSetInactive,
}: EmployeeInformationProps) => {
  const { t, i18n } = useTranslation('common');
  const isAdpWfnCanada = useIsAdpWfnCanadaConnectedOrg();
  const { ithManagerField, ithNewItSetup } = useFlags();
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  const navigate = useNavigate();
  const {
    clearErrors,
    control,
    trigger,
    getValues,
    reset,
    setError,
    setValue,
  } = useFormContext<OnboardingFormData>();
  const { isValid, isSubmitting } = useFormState();
  const [firstName, lastName] = useWatch({
    name: ['demographics.firstName', 'demographics.lastName'],
    control,
  });
  const fullName = getFullName(firstName, lastName);
  const [getEmployeeByEmail] = useLazyGetEmployeeByEmailQuery();
  const organizationId = useGetOrganizationId();

  /**
   * The user can proceed to the Onboarding step when they have the permission to onboard and the employee is a new hire
   */
  const onboardingStepEnabled = isNewHire && canOnboard;
  /**
   * When the Onboarding step is enabled or the organization has multiple packages, show the "Next" button to proceed to either the Onboarding or the License Selection step.
   * Otherwise, show the "Finish" button to end the flow.
   */
  const nextButtonLabel =
    onboardingStepEnabled || isMultiLicense
      ? t('onboardings.employeeInformation.nextButton')
      : t('onboardings.employeeInformation.finishButton');
  /**
   * Show the "Finish and add another" button when all the following rules are met:
   * - The Onboarding step is disabled
   * - The organization does not have multiple packages
   *   - If it's a multi-plan organization, the button will be displayed in the following step (License Selection)
   * - The organization has available licenses
   */
  const showFinishAndAddButton =
    !onboardingStepEnabled && !isMultiLicense && hasAvailableLicenses;

  const validateEmail = async () => {
    const email = getValues('employeeInformation.email');
    setIsValidatingEmail(true);

    try {
      const employee = await getEmployeeByEmail({
        organizationId,
        email,
      }).unwrap();
      const message =
        employee.status === EmployeeStatusEnum.Inactive
          ? t('onboardings.employeeInformation.emailAlreadyExistsInactive')
          : t('onboardings.employeeInformation.emailAlreadyExists');

      setError('employeeInformation.email', { message });
      return false;
    } catch (error) {
      if (isFetchBaseQueryError(error) && error.status === 404) {
        clearErrors('employeeInformation.email');
        return true;
      }
      throw error;
    } finally {
      setIsValidatingEmail(false);
    }
  };

  const handlePrevious = () => {
    const currentStep = getValues('currentStep');
    setValue('currentStep', currentStep - 1, {
      shouldValidate: true,
    });
  };

  const handleNext = async () => {
    const isValidEmail = await validateEmail();
    if (!isValidEmail) return;

    // If the organization has multiple packages, go to the License Selection step.
    if (isMultiLicense) {
      setValue('currentStep', OnboardingStep.LicenseSelection, {
        shouldValidate: true,
      });
      return;
    }

    // If the employee is a new hire and the user has permission to create an onboarding, go to the Onboarding step.
    if (onboardingStepEnabled) {
      setValue('currentStep', OnboardingStep.Onboarding, {
        shouldValidate: true,
      });
      return;
    }

    // Otherwise, add the employee and go to their profile.
    // As the organization at this point is single-plan, the organization package id is defined by default in the backend when the employee is set as ACTIVE.
    addEmployee({
      onSuccess: (employeeId: string) => {
        if (employeeId) {
          navigate(`/employees/${employeeId}`);
        }
      },
      status: EmployeeStatusEnum.Active,
    });
  };

  // Add the current employee and return to the form start to add more employees.
  // This action is available only when the organization has a single package, so the package id will be set by default in the backend.
  const handleAddMore = async () => {
    const isValidEmail = await validateEmail();
    if (!isValidEmail) return;

    addEmployee({
      onSuccess: (employeeId: string) => {
        if (employeeId) {
          reset();
          setValue('currentStep', OnboardingStep.Demographics);
        }
      },
      status: EmployeeStatusEnum.Active,
    });
  };

  const employees = useOrganizationEmployees();
  const employeeOptions = transformEmployeesToOptions(
    employees,
    i18n.language,
    isAdpWfnCanada
  );

  const isLoading = isValidatingEmail || isSubmitting;

  return (
    <>
      <Header
        title={t('onboardings.employeeInformation.title')}
        subtitle={t('onboardings.employeeInformation.subtitle', { fullName })}
      />
      <Flex width="100%" vertical>
        <DateField
          id="startDate"
          name="employeeInformation.startDate"
          control={control}
          label={t('onboardings.employeeInformation.startDateLabel')}
          placeholder=""
          required={isNewHire}
          minDate={null} // No min date
        />
        <TextInputField
          id="jobTitle"
          name="employeeInformation.jobTitle"
          control={control}
          label={t('onboardings.employeeInformation.titleLabel')}
          required={isNewHire}
        />
        <TextInputField
          id="email"
          name="employeeInformation.email"
          control={control}
          label={t('onboardings.employeeInformation.workEmailLabel')}
          required
        />
        {ithManagerField && (
          <EmployeeManagerSelector
            id="onboarding-manager-search-selector"
            name="employeeInformation.manager"
            control={control}
            label={t('onboardings.employeeInformation.managerLabel')}
            organizationId={organizationId}
          />
        )}
      </Flex>
      {!ithNewItSetup && (
        <>
          <Text variant="heading-2" mt="24px">
            {t('onboardings.employeeInformation.notificationsHeading')}
          </Text>
          <Text variant="body" mb="24px">
            {t('onboardings.employeeInformation.notificationsDescription')}
          </Text>
          <Flex width="100%" vertical>
            <SearchableMultiSelectField
              id="watchers"
              name="watchers"
              control={control}
              trigger={trigger}
              label={t(
                'onboardings.employeeInformation.additionalFollowersLabel'
              )}
              placeholder={t(
                'onboardings.employeeInformation.additionalFollowersPlaceholder'
              )}
              options={employeeOptions}
              customComponents={{
                Option: option => <EmployeeSelectOption data={option.data} />,
              }}
              searchableFields={['name', 'email']}
              helperText={t(
                'onboardings.employeeInformation.additionalFollowersHelperText'
              )}
            />
          </Flex>
        </>
      )}
      <FormActionsContainer>
        <Button
          id="onboarding-employee-info-previous-button"
          variant="outline"
          onClick={handlePrevious}
          disabled={isLoading}>
          {t('onboardings.employeeInformation.previousButton')}
        </Button>
        <Flex gap="2rem">
          {showFinishAndAddButton && (
            <Button
              id="onboarding-employee-info-finish-and-add-button"
              variant="text"
              onClick={handleAddMore}
              disabled={!isValid || isLoading}>
              {t('onboardings.employeeInformation.finishAndAddButton')}
            </Button>
          )}
          {!hasAvailableLicenses && (
            <Button
              id="onboarding-set-as-inactive-button"
              variant="text"
              onClick={handleSetInactive}
              disabled={isSubmitting}>
              {t('onboardings.employeeInformation.setAsInactiveButton')}
            </Button>
          )}
          <Button
            id="onboarding-employee-info-next-button"
            onClick={handleNext}
            disabled={!isValid || !hasAvailableLicenses}
            loading={isLoading}>
            {nextButtonLabel}
          </Button>
        </Flex>
      </FormActionsContainer>
    </>
  );
};

export default EmployeeInformation;
